/**
 * Balance-query detail (PR-6a).
 *
 * Shows the dispatch correlators, the result envelope (when settled),
 * and the projected per-account balances. Links back to the inbox row
 * that delivered the result for forensic drill-down.
 */
import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ArrowLeft } from "lucide-react";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/components/ui/sonner";
import { getBalanceQuery, type BalanceQueryRow, type BalanceQueryState } from "@/lib/api";
import { formatMinorUnits } from "@/lib/format";

const STATE_VARIANT: Record<BalanceQueryState, BadgeProps["variant"]> = {
  dispatched: "info",
  settled: "success",
  failed: "destructive",
  stale: "warning",
};

export function BalanceQueryDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const query = useQuery({
    queryKey: ["balance-query", id] as const,
    queryFn: () => getBalanceQuery(id),
    enabled: Boolean(id),
  });

  useEffect(() => {
    if (query.error) {
      toast.error("Couldn't load this balance query", {
        description: "It may not exist or you may not have access.",
      });
    }
  }, [query.error]);

  if (query.isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (query.error || !query.data) {
    return (
      <Card className="border-destructive/40 bg-destructive/5">
        <CardHeader className="flex flex-row items-start gap-3 space-y-0">
          <AlertTriangle className="h-5 w-5 shrink-0 text-destructive" />
          <div>
            <CardTitle className="text-base">Couldn't load this query</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              It may not exist or you may not have access.{" "}
              <Link to="/balance-queries" className="text-foreground underline">
                Back to list
              </Link>
            </p>
          </div>
        </CardHeader>
      </Card>
    );
  }
  return <Loaded row={query.data} />;
}

function Loaded({ row }: { row: BalanceQueryRow }) {
  return (
    <>
      <div className="mb-6 space-y-3">
        <Button
          asChild
          variant="ghost"
          size="sm"
          className="-ml-2 h-auto px-2 text-muted-foreground"
        >
          <Link to="/balance-queries">
            <ArrowLeft />
            Back to balance queries
          </Link>
        </Button>

        <Card className="p-5 sm:p-6">
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-lg font-semibold">Balance query</span>
              <Badge variant={STATE_VARIANT[row.state]} className="capitalize">
                {row.state}
              </Badge>
              <Badge variant="muted" className="capitalize">
                {row.tenant.environment}
              </Badge>
            </div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{row.tenant.name}</span>
              <span>·</span>
              <span>
                {row.provider_account.plugin} · {row.provider_account.name}
              </span>
              <span>·</span>
              <span title={row.created_at}>
                dispatched {new Date(row.created_at).toLocaleString()}
              </span>
            </div>
            <code className="block truncate font-mono text-xs text-muted-foreground/80">
              {row.id}
            </code>
          </div>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Correlators</CardTitle>
          </CardHeader>
          <CardContent>
            <Defs>
              <Def label="Originator conversation ID" mono>
                {row.originator_conversation_id}
              </Def>
              <Def label="Conversation ID" mono>
                {row.conversation_id ?? "—"}
              </Def>
              <Def label="Inbox row" mono>
                {row.inbox_row_id ? (
                  <Link
                    to={`/callback-inbox/${row.inbox_row_id}`}
                    className="underline"
                  >
                    {row.inbox_row_id}
                  </Link>
                ) : (
                  "—"
                )}
              </Def>
            </Defs>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Result envelope</CardTitle>
          </CardHeader>
          <CardContent>
            <Defs>
              <Def label="Received">
                {row.result_received_at
                  ? new Date(row.result_received_at).toLocaleString()
                  : "—"}
              </Def>
              <Def label="Result code" mono>
                {row.result_code ?? "—"}
              </Def>
              <Def label="Result desc">{row.result_desc ?? "—"}</Def>
              <Def label="Dropped chunks">{row.dropped_chunks}</Def>
            </Defs>
          </CardContent>
        </Card>
      </div>

      <Card className="mt-4">
        <CardHeader>
          <CardTitle className="text-base">Projected accounts</CardTitle>
          <p className="text-xs text-muted-foreground">
            Parsed from the AccountBalance result envelope. Empty when the
            query is still in flight, failed, or returned no balances.
          </p>
        </CardHeader>
        <CardContent>
          {!row.accounts || row.accounts.length === 0 ? (
            <p className="text-sm text-muted-foreground">No accounts projected.</p>
          ) : (
            <ul className="space-y-2">
              {row.accounts.map((acct, i) => (
                <li
                  key={`${acct.label}-${i}`}
                  className="flex items-baseline justify-between rounded-md border bg-muted/20 px-3 py-2 text-sm"
                >
                  <span className="font-medium">{acct.label}</span>
                  <span className="font-semibold tabular-nums">
                    {formatMinorUnits(String(acct.amount), acct.currency)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </>
  );
}

function Defs({ children }: { children: React.ReactNode }) {
  return <dl className="space-y-3">{children}</dl>;
}

function Def({
  label,
  children,
  mono = false,
}: {
  label: string;
  children: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
      <dt className="text-xs uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      <dd
        className={
          mono
            ? "min-w-0 break-all font-mono text-xs sm:text-right"
            : "min-w-0 text-sm sm:text-right"
        }
      >
        {children}
      </dd>
    </div>
  );
}

export default BalanceQueryDetailPage;
